<template>
  <div v-if="isVisible" class="top-bar">
    <v-btn rounded color="#1baaf1" small depressed :disabled="!isVisible || cancelLoading" :loading="cancelLoading"
      @click="cancel">
      투자 모집 취소
    </v-btn>
  </div>
</template>

<script>
// import { mapActions, mapState } from 'vuex'
import LoanService from '@/service/loan/LoanService'
import loanApi from '@/api/now/loanApi'
import contractsApi from '@/api/now/contractsApi'
import StatusService from '@/service/status/StatusService'
// import { sleep } from '@/util'
import moment from 'moment'

export default {
  data() {
    return {
      loan: null,
      loanContractLoading: true,
      loanContract: null,
      cancelLoading: false,
    }
  },
  props: {
    status: {
      type: Object
    },
  },
  computed: {
    isVisible() {
      return !this.loanContractLoading && !this.loanContract && this.isLoanCancelable
    },
    isLoanCancelable() {
      return [
        StatusService.statuses.INVESTING.code,
        StatusService.statuses.INVESTED.code
      ].includes(this.status.code)
    },
    isCancelableTime() {
      // 23시 ~ 1시까지 취소 불가(1시부터 가능?)
      const nowHour = moment().hour()
      // 1시 0분 ~ 22시 59분 까지 가능
      return nowHour > 0 && nowHour < 23
    }
  },
  async created() {
    this.loan = await LoanService.getCurrentLoan()
    await this.getLoanContractAgreement()
  },
  methods: {
    /**
     * 계약서 작성됐는지 여부 확인
     */
    async getLoanContractAgreement() {
      const { data } = await contractsApi.getAgreements()
      this.loanContract = data?.find(agreement => agreement.offer && agreement.offer.uuid === this.loan.offer.uuid)
      this.loanContractLoading = false

      return this.loanContract
    },

    async swalAlert(html) {
      await this.$swal.fire({
        html: `<p class="custom-alert__text">
          ${html}
        </p>`,
        confirmButtonText: '확인',
        customClass: { container: 'custom-alert2', actions: 'single' }
      })
    },

    async cancel() {
      if (this.cancelLoading) {
        return
      }

      // 종료 전 새로고침 실행 여부
      let isFinallyReload = false
      try {
        this.cancelLoading = true

        // 취소 요청 시간 검사
        if (!this.isCancelableTime) {
          await this.swalAlert(`
            금융결제원 서버 점검 관계로<br />
            23시부터 01시까지는 투자 모집 취소가 불가능합니다.
          `)
          return
        }

        // 취소 여부 재확인
        const { isConfirmed } = await this.$swal.fire({
          html: `
          모집된 금액은 <br />
          투자자에게 모두 반환됩니다. <br />
          정말 취소하시겠습니까?
        `,
          showCloseButton: false,
          showCancelButton: true,
          focusConfirm: false,
          confirmButtonText: '확인',
          cancelButtonText: '취소',
          reverseButtons: true,
          customClass: { container: 'custom-alert2' },
        })
        if (!isConfirmed) {
          return
        }
        // 성공이든 실패든 마지막에 새로고침
        isFinallyReload = true

        // 로딩 화면 노출
        this.$swal.fire({
          html: `
            <div style="height: 80px; position: relative; margin-bottom: 20px;">
              <span class="v-btn__loader">
                <div role="progressbar" aria-valuemin="0" aria-valuemax="100" class="v-progress-circular v-progress-circular--indeterminate" style="height: 80px; width: 80px;">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="21.904761904761905 21.904761904761905 43.80952380952381 43.80952380952381" style="transform: rotate(0deg);">
                    <circle fill="transparent" cx="43.80952380952381" cy="43.80952380952381" r="20" stroke-width="3.8095238095238093" stroke-dasharray="125.664" stroke-dashoffset="125.66370614359172px" class="v-progress-circular__overlay"></circle>
                    </svg>
                    <div class="v-progress-circular__info">
                    </div>
                </div>
              </span>
            </div>
            <p style="font-weight: 700">취소 처리중입니다.</p>
            <p style="font-size: 14px">
              최대 5분정도 소요될 수 있으니 페이지에서 벗어나지 마시고 잠시만 기다려주세요.
            </p>
          `,
          iconHtml: 'circle',
          showCloseButton: false,
          showCancelButton: false,
          showConfirmButton: false,
          customClass: { container: 'custom-alert2', popup: 'justify-center' },
          timerProgressBar: true,
          allowOutsideClick: false,
          allowEscapeKey: false,
          allowEnterKey: false,
        })

        // 취소 로직 실행
        await this._cancel()
      } finally {
        this.cancelLoading = false
        if (isFinallyReload) {
          location.reload()
        }
      }
    },

    async _cancel() {
      // 계약서 및 대출 상태 조회
      const [agree, { data: loan }] = await Promise.all([this.getLoanContractAgreement(), loanApi.getLoan(this.loan.uuid)])

      // 대출 조회해서 상태 검사
      const isInvestStatus = [StatusService.statuses.INVESTING.code, StatusService.statuses.INVESTED.code].includes(loan.status)
      if (!isInvestStatus) {
        let message = ''

        if (loan.status === 'canceled') {
          message = '이미 취소된 대출입니다.'
        } else if (loan.status === 'executing') {
          message = '이미 실행중인 대출은 취소할 수 없습니다.'
        } else {
          // 그 외 executed finished
          message = '이미 실행이 완료된 대출입니다.'
        }

        await this.swalAlert(message)
        return
      }

      // 계약서 작성 여부 검사
      if (agree) {
        // 작성된 계약서가 존재하는 경우
        await this.swalAlert('이미 계약서가 작성되어 취소할 수 없습니다.')
        return
      }

      // 취소 api 실행
      try {
        await loanApi.cancelLoan(this.loan.uuid)
        await this.swalAlert('취소가 완료되었습니다.')
      } catch (e) {
        await this.swalAlert('대출 취소중 오류가 발생했습니다. 고객센터로 문의주세요.')
        throw e
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.top-bar {
  display: flex;
  justify-content: flex-end;
  padding-bottom: 15px;

  button::v-deep span.v-btn__content {
    color: white;
  }
}
</style>
